/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import { DetailedUserDto, AdminUsersListDto, NewUserData, UserStatus } from './usersClient.typings';
import { detailedUserFactory } from './usersFactories';

const ADMIN_USERS_LIST_URL = /\/users$/;
const USER_DETAILS_URL = /\/users\/\d+/;
const USER_PROFILE_URL = /\/users\/profile/;
const USER_PASSWORD_URL = /\/users\/\d+\/password/;
const BLOCK_USER_URL = /\/users\/\d+\/block/;
const UNBLOCK_USER_URL = /\/users\/\d+\/unblock/;
const DELETE_USER_URL = /\/users\/\d+\/delete/;
const ADD_USER_URL = /\/users\/add/;

class UsersFeatureMock extends BaseFeatureMock {
    public readonly listUsers = this.route<AdminUsersListDto>({
        method: 'get',
        url: ADMIN_USERS_LIST_URL,
        defaultResponse: () => {
            const data: AdminUsersListDto = {
                count: 40,
                users: detailedUserFactory.list({ count: 20 }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly userDetails = this.route<DetailedUserDto>({
        method: 'get',
        url: USER_DETAILS_URL,
        defaultResponse: () => {
            const data: DetailedUserDto = detailedUserFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly userProfile = this.route<DetailedUserDto>({
        method: 'patch',
        url: USER_PROFILE_URL,
        defaultResponse: () => {
            const data: DetailedUserDto = detailedUserFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly changePassword = this.route<null>({
        method: 'patch',
        url: USER_PASSWORD_URL,
        defaultResponse: () => {
            return [200 /* 403 */, null];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly blockUser = this.route<DetailedUserDto>({
        method: 'patch',
        url: BLOCK_USER_URL,
        defaultResponse: (config) => {
            const data: DetailedUserDto = detailedUserFactory.item();
            data.id = parseInt(config.url.split('/')[2]);
            data.status = UserStatus.Inactive;

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly unblockUser = this.route<DetailedUserDto>({
        method: 'patch',
        url: UNBLOCK_USER_URL,
        defaultResponse: (config) => {
            const data: DetailedUserDto = detailedUserFactory.item();
            data.id = parseInt(config.url.split('/')[2]);
            data.status = UserStatus.Active;

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly deleteUser = this.route<DetailedUserDto>({
        method: 'delete',
        url: DELETE_USER_URL,
        defaultResponse: (config) => {
            const data: DetailedUserDto = detailedUserFactory.item();
            data.id = parseInt(config.url.split('/')[2]);
            data.status = UserStatus.Deleted;

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly addUser = this.route<DetailedUserDto, NewUserData>({
        method: 'post',
        url: ADD_USER_URL,
        defaultResponse: (config) => {
            const data: DetailedUserDto = detailedUserFactory.item();
            const updatedFields: NewUserData = JSON.parse(config.data);

            for (const key in updatedFields) {
                if (data.hasOwnProperty(key)) {
                    data[key as keyof DetailedUserDto] = updatedFields[key as keyof NewUserData];
                }
            }

            return [200 /* 403 */, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly updateUser = this.route<DetailedUserDto, NewUserData>({
        method: 'patch',
        url: USER_DETAILS_URL,
        defaultResponse: (config) => {
            const data: DetailedUserDto = detailedUserFactory.item();
            data.id = parseInt(config.url.split('/')[2]);
            const updatedFields: NewUserData = JSON.parse(config.data);

            for (const key in updatedFields) {
                if (data.hasOwnProperty(key)) {
                    data[key as keyof DetailedUserDto] = updatedFields[key as keyof NewUserData];
                }
            }

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    protected onReset(): void {
        detailedUserFactory.resetCount();
    }
}

export const usersMock = new UsersFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addUsersMockApi(mockAdapter: MockAdapter): void {
    usersMock.register(mockAdapter, { delay: true });
}
